import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip
} from '@mui/material';
import {
  Person as PersonIcon,
  PersonAdd as GuestIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Notes as NotesIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { coachingProgramAPI } from '../../services/api';
import { CoachingProgram, CoachingProgramEnrollment } from '../../types/coaching';

interface ProgramEnrollmentsViewProps {
  program: CoachingProgram;
  onEnrollmentUpdated?: () => void;
  venueNames?: Map<string, string>;
}

const ProgramEnrollmentsView: React.FC<ProgramEnrollmentsViewProps> = ({
  program,
  onEnrollmentUpdated,
  venueNames
}) => {
  const [enrollments, setEnrollments] = useState<CoachingProgramEnrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingEnrollmentId, setCancellingEnrollmentId] = useState<string | null>(null);
  const [confirmCancelDialog, setConfirmCancelDialog] = useState<{
    open: boolean;
    enrollment: CoachingProgramEnrollment | null;
  }>({ open: false, enrollment: null });

  useEffect(() => {
    fetchEnrollments();
  }, [program.id]);

  const fetchEnrollments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await coachingProgramAPI.getCoachingProgramEnrollments(program.id);
      setEnrollments(response.data);
    } catch (error: any) {
      console.error('Error fetching enrollments:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch enrollments';
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEnrollment = (enrollment: CoachingProgramEnrollment) => {
    setConfirmCancelDialog({ open: true, enrollment });
  };

  const confirmCancelEnrollment = async () => {
    if (!confirmCancelDialog.enrollment) return;

    try {
      setCancellingEnrollmentId(confirmCancelDialog.enrollment.id);
      await coachingProgramAPI.cancelEnrollment(program.id, confirmCancelDialog.enrollment.id);
      
      // Refresh enrollments and notify parent
      await fetchEnrollments();
      onEnrollmentUpdated?.();
      
      setConfirmCancelDialog({ open: false, enrollment: null });
    } catch (error: any) {
      console.error('Error cancelling enrollment:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to cancel enrollment';
      
      setError(errorMessage);
    } finally {
      setCancellingEnrollmentId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ENROLLED':
        return 'success';
      case 'WAITLIST':
        return 'warning';
      case 'DROPPED':
        return 'error';
      case 'COMPLETED':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number | string | undefined) => {
    if (!amount) return '0.00';
    const numericAmount = typeof amount === 'number' ? amount : parseFloat(amount.toString());
    return isNaN(numericAmount) ? '0.00' : numericAmount.toFixed(2);
  };

  const calculateTotalRevenue = () => {
    const confirmedEnrollments = enrollments.filter(e => e.status === 'ENROLLED');
    const total = confirmedEnrollments.reduce((sum, enrollment) => {
      const amount = enrollment.totalPaid || 0;
      return sum + (typeof amount === 'number' ? amount : 0);
    }, 0);
    return total.toFixed(2);
  };

  const getEnrollmentTypeLabel = (enrollment: CoachingProgramEnrollment) => {
    return enrollment.enrollmentType === 'FULL_PROGRAM' ? 'Full Program' : 'Drop-in';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with program summary */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Coach:</strong> {program.coachName}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Venue:</strong> {
                program.venues && program.venues.length > 0 
                  ? program.venues.map(id => venueNames?.get(id) || id).join(', ')
                  : 'Not specified'
              }
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Enrollments:</strong> {enrollments.filter(e => e.status === 'ENROLLED').length} / {program.capacity}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Revenue:</strong> ${calculateTotalRevenue()}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Enrollments ({enrollments.filter(e => e.status === 'ENROLLED').length})
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchEnrollments}
          disabled={loading}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Enrollments List */}
      {enrollments.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            No enrollments yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This program hasn't been joined by anyone
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {enrollments.map((enrollment) => (
            <Grid size={{ xs: 12 }} key={enrollment.id}>
              <Card 
                variant="outlined" 
                sx={{ 
                  position: 'relative',
                  opacity: enrollment.status === 'DROPPED' ? 0.7 : 1,
                  borderColor: enrollment.status === 'DROPPED' ? 'error.main' : 'divider',
                  '&::before': enrollment.status === 'DROPPED' ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(128, 128, 128, 0.2)',
                    borderRadius: 'inherit',
                    zIndex: 1,
                    pointerEvents: 'none'
                  } : {}
                }}
              >
                <CardContent sx={{ position: 'relative', zIndex: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {enrollment.isGuestEnrollment ? (
                        <GuestIcon color={enrollment.status === 'DROPPED' ? 'disabled' : 'action'} />
                      ) : (
                        <PersonIcon color={enrollment.status === 'DROPPED' ? 'disabled' : 'action'} />
                      )}
                      <Typography 
                        variant="h6"
                        sx={{
                          color: enrollment.status === 'DROPPED' ? 'text.disabled' : 'inherit',
                          textDecoration: enrollment.status === 'DROPPED' ? 'line-through' : 'none'
                        }}
                      >
                        {enrollment.isGuestEnrollment ? enrollment.guestName : enrollment.memberName}
                      </Typography>
                      <Chip
                        label={enrollment.status === 'DROPPED' ? 'CANCELLED' : enrollment.status}
                        color={getStatusColor(enrollment.status)}
                        size="small"
                      />
                      <Chip
                        label={getEnrollmentTypeLabel(enrollment)}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                    
                    {enrollment.status === 'ENROLLED' && (
                      <Tooltip title="Remove from program" arrow>
                        <IconButton
                          color="error"
                          onClick={() => handleCancelEnrollment(enrollment)}
                          disabled={cancellingEnrollmentId === enrollment.id}
                          size="small"
                          sx={{ 
                            '&:hover': { 
                              backgroundColor: 'error.light',
                              color: 'white'
                            } 
                          }}
                        >
                          {cancellingEnrollmentId === enrollment.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CancelIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    {/* Contact Info */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon 
                            fontSize="small" 
                            color={enrollment.status === 'DROPPED' ? 'disabled' : 'action'} 
                          />
                          <Typography 
                            variant="body2"
                            color={enrollment.status === 'DROPPED' ? 'text.disabled' : 'inherit'}
                          >
                            {enrollment.isGuestEnrollment ? enrollment.guestEmail : enrollment.memberEmail}
                          </Typography>
                        </Box>
                        
                        {enrollment.isGuestEnrollment && (
                          <Typography 
                            variant="caption" 
                            color={enrollment.status === 'DROPPED' ? 'text.disabled' : 'text.secondary'}
                          >
                            Guest Enrollment
                          </Typography>
                        )}

                        {enrollment.waitlistPosition && (
                          <Typography 
                            variant="caption" 
                            color="warning.main"
                          >
                            Waitlist Position: {enrollment.waitlistPosition}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>

                    {/* Enrollment Details */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon 
                            fontSize="small" 
                            color={enrollment.status === 'DROPPED' ? 'disabled' : 'action'} 
                          />
                          <Typography 
                            variant="body2"
                            color={enrollment.status === 'DROPPED' ? 'text.disabled' : 'inherit'}
                          >
                            Sessions Attended: {enrollment.sessionsAttended || 0}
                          </Typography>
                        </Box>
                        
                        {enrollment.totalPaid && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MoneyIcon 
                              fontSize="small" 
                              color={enrollment.status === 'DROPPED' ? 'disabled' : 'action'} 
                            />
                            <Typography 
                              variant="body2"
                              color={enrollment.status === 'DROPPED' ? 'text.disabled' : 'inherit'}
                            >
                              Paid: ${formatCurrency(enrollment.totalPaid)}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Grid>

                    {/* Notes */}
                    {enrollment.notes && (
                      <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <NotesIcon 
                            fontSize="small" 
                            color={enrollment.status === 'DROPPED' ? 'disabled' : 'action'} 
                          />
                          <Typography 
                            variant="body2" 
                            color={enrollment.status === 'DROPPED' ? 'text.disabled' : 'text.secondary'}
                          >
                            {enrollment.notes}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {/* Enrollment metadata */}
                  <Box sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      Enrolled on {new Date(enrollment.createdAt).toLocaleDateString()} at {new Date(enrollment.createdAt).toLocaleTimeString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={confirmCancelDialog.open}
        onClose={() => setConfirmCancelDialog({ open: false, enrollment: null })}
      >
        <DialogTitle>Remove Member from Program</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove{' '}
            <strong>
              {confirmCancelDialog.enrollment?.isGuestEnrollment
                ? confirmCancelDialog.enrollment.guestName
                : confirmCancelDialog.enrollment?.memberName}
            </strong>
            {' '}from this program?
            {'\n\n'}
            This will mark their enrollment as cancelled. This action cannot be undone and may affect billing.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmCancelDialog({ open: false, enrollment: null })}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmCancelEnrollment}
            color="error" 
            variant="contained"
            disabled={cancellingEnrollmentId !== null}
            startIcon={cancellingEnrollmentId ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {cancellingEnrollmentId ? 'Removing...' : 'Remove from Program'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProgramEnrollmentsView;
